import { IconButton } from '@creditornot/cb-button';
import { InputCheckbox } from '@creditornot/cb-checkbox';
import { MinusCircleFilled } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors, WoltLogo, typographyCss } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Tag } from '@creditornot/cb-tag';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';

const BaseRow = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  cursor: pointer;
`;

const Wrapper = styled(BaseRow)<{ checked: boolean }>`
  ${typographyCss.Body3()}
  background-color: ${({ checked }) => (checked ? colors.wolt4 : 'transparent')};
  padding-inline-start: 8px;
  padding-inline-end: 8px;
  &:hover {
    background-color: ${colors.wolt4};
  }
`;

const StyledCheckbox = styled(InputCheckbox)`
  margin-inline-end: 16px;
`;

const StyledMinusIconButton = styled(IconButton)`
  margin-inline-end: 16px;
`;

const RowContent = styled.div`
  display: flex;
  align-items: center;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background-color: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  overflow: hidden;
`;

const Thumbnail = styled.img`
  width: 32px;
  height: 32px;
  object-fit: cover;
`;

const venueNameCss = css`
  flex: 1;
  display: flex;
  margin-inline-start: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VenueName = styled(T.Body3)`
  ${venueNameCss}
`;

const VenueAddress = styled(T.Small)`
  ${venueNameCss}
`;

const PlaceholderContainer = styled(ImageContainer)`
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const PlaceholderImage = styled(WoltLogo)`
  width: 18px;
  fill: ${cbSemanticCssVars.textTertiary};
`;

type VenueRowProps = {
  checked?: boolean;
  venue: Venue;
  onCheck?: (id: string, checked: boolean) => void;
  onRemove?: () => void;
  disabled?: boolean;
};

type VenueStatus = 'active' | 'inactive';

const VENUE_STATUS: { [key in VenueStatus]: string } = {
  active: 'purchases.merchant.status-active',
  inactive: 'purchases.merchant.status-inactive',
};

export const VenueRow = ({ venue, checked = false, onCheck, onRemove, disabled }: VenueRowProps) => {
  const intl = useIntl();
  const venueName = localize(venue.name, intl.locale);
  const status: VenueStatus = venue.status === 'VENUE_PUBLISHED' ? 'active' : 'inactive';

  const tagVariant = status === 'active' ? 'lightBlue' : 'lightRed';

  const handleCheck = (id: string, checked: boolean) => {
    if (onCheck) {
      onCheck(id, checked);
    }
  };

  return (
    <Wrapper checked={checked}>
      <RowContent>
        {onCheck && (
          <StyledCheckbox
            role="checkbox"
            disabled={disabled}
            checked={checked}
            onChange={() => handleCheck(venue.id, !checked)}
          />
        )}

        {onRemove && (
          <StyledMinusIconButton onClick={onRemove} variant="subdued" size="small" background="transparent">
            <MinusCircleFilled />
          </StyledMinusIconButton>
        )}

        {venue.listimage ? (
          <ImageContainer>
            <Thumbnail src={venue.listimage} />
          </ImageContainer>
        ) : (
          <PlaceholderContainer>
            <PlaceholderImage />
          </PlaceholderContainer>
        )}
        <div>
          <VenueName>{venueName}</VenueName>
          <VenueAddress>{venue.address}</VenueAddress>
        </div>
      </RowContent>

      <RowContent>
        <Tag variant={tagVariant} uppercase>
          <FormattedMessage id={VENUE_STATUS[status]} />
        </Tag>
      </RowContent>
    </Wrapper>
  );
};
